import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../services/AxiosInstance";

// ============================================================
// ZERO BILL REPORT
// ============================================================

export const fetchZeroBillReportThunk = createAsyncThunk(
  "zeroBillReport/fetch",
  async (
    {
      bill_type = "water",
      ward_id = "",
      mtr_type = "",
      slrm_type = "",
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const params = {
        bill_type,
      };

      if (ward_id) {
        params.ward_id = ward_id;
      }

      if (bill_type === "water" && mtr_type) {
        params.mtr_type = mtr_type;
      }

      if (bill_type === "slrm" && slrm_type) {
        params.slrm_type = slrm_type;
      }

      const { data } = await api.get("/api/reports/zero-bills", {
        params,
      });

      if (data?.success === false || data?.status === false) {
        return rejectWithValue(
          data?.message || "Failed to load zero bill report"
        );
      }

      const rows = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.rows)
          ? data.data.rows
          : Array.isArray(data?.rows)
            ? data.rows
            : [];

      return {
        rows,
        bill_type,
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load zero bill report"
      );
    }
  }
);

// ============================================================
// OVERALL SUMMARY REPORT
// ============================================================

export const fetchOverallSummaryReportThunk = createAsyncThunk(
  "zeroBillReport/fetchOverallSummary",
  async (
    {
      from_date,
      to_date,

      panchayath_page = 1,
      panchayath_limit = 10,

      agent_page = 1,
      agent_limit = 10,

      office_page = 1,
      office_limit = 10,

      export_all = false,
    } = {},
    { rejectWithValue }
  ) => {
    try {
      if (!from_date || !to_date) {
        return rejectWithValue(
          "From Date and To Date are required"
        );
      }

      const params = {
        from_date,
        to_date,

        panchayath_page,
        panchayath_limit,

        agent_page,
        agent_limit,

        office_page,
        office_limit,
      };

      if (export_all) {
        params.export = 1;
      }

      const { data } = await api.get(
        "/api/reports/overall-summary-report",
        {
          params,
        }
      );

      if (data?.success === false || data?.status === false) {
        return rejectWithValue(
          data?.message ||
            "Failed to load overall summary report"
        );
      }

      const reportData = data?.data || {};

      return {
        filters: data?.filters || {
          from_date,
          to_date,
        },

        panchayathSummary: {
          rows: Array.isArray(
            reportData?.panchayath_summary?.rows
          )
            ? reportData.panchayath_summary.rows
            : [],

          pagination:
            reportData?.panchayath_summary?.pagination || {
              page: panchayath_page,
              limit: panchayath_limit,
              total: 0,
              total_pages: 0,
            },
        },

        agentSummary: {
          rows: Array.isArray(
            reportData?.agent_summary?.rows
          )
            ? reportData.agent_summary.rows
            : [],

          pagination:
            reportData?.agent_summary?.pagination || {
              page: agent_page,
              limit: agent_limit,
              total: 0,
              total_pages: 0,
            },
        },

        officeUserSummary: {
          rows: Array.isArray(
            reportData?.office_user_summary?.rows
          )
            ? reportData.office_user_summary.rows
            : [],

          pagination:
            reportData?.office_user_summary?.pagination || {
              page: office_page,
              limit: office_limit,
              total: 0,
              total_pages: 0,
            },
        },
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load overall summary report"
      );
    }
  }
);

const initialPagination = {
  page: 1,
  limit: 10,
  total: 0,
  total_pages: 0,
};

const zeroBillReportSlice = createSlice({
  name: "zeroBillReport",

  initialState: {
    // Zero bill report
    rows: [],
    billType: "water",
    loading: false,
    loaded: false,
    error: null,

    // Overall summary report
    overallSummary: {
      filters: {
        from_date: "",
        to_date: "",
        total_days: 0,
        pan_ids: [],
        export: 0,
      },

      panchayathSummary: {
        rows: [],
        pagination: { ...initialPagination },
      },

      agentSummary: {
        rows: [],
        pagination: { ...initialPagination },
      },

      officeUserSummary: {
        rows: [],
        pagination: { ...initialPagination },
      },

      loading: false,
      loaded: false,
      error: null,
    },
  },

  reducers: {
    clearZeroBillReport: (state) => {
      state.rows = [];
      state.billType = "water";
      state.loading = false;
      state.loaded = false;
      state.error = null;
    },

    clearZeroBillReportError: (state) => {
      state.error = null;
    },

    clearOverallSummaryReport: (state) => {
      state.overallSummary = {
        filters: {
          from_date: "",
          to_date: "",
          total_days: 0,
          pan_ids: [],
          export: 0,
        },

        panchayathSummary: {
          rows: [],
          pagination: { ...initialPagination },
        },

        agentSummary: {
          rows: [],
          pagination: { ...initialPagination },
        },

        officeUserSummary: {
          rows: [],
          pagination: { ...initialPagination },
        },

        loading: false,
        loaded: false,
        error: null,
      };
    },

    clearOverallSummaryReportError: (state) => {
      state.overallSummary.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // ======================================================
      // ZERO BILL REPORT
      // ======================================================

      .addCase(fetchZeroBillReportThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(
        fetchZeroBillReportThunk.fulfilled,
        (state, action) => {
          state.loading = false;
          state.loaded = true;
          state.rows = action.payload?.rows || [];
          state.billType =
            action.payload?.bill_type || "water";
          state.error = null;
        }
      )

      .addCase(
        fetchZeroBillReportThunk.rejected,
        (state, action) => {
          state.loading = false;
          state.loaded = true;
          state.rows = [];
          state.error =
            action.payload ||
            "Failed to load zero bill report";
        }
      )

      // ======================================================
      // OVERALL SUMMARY REPORT
      // ======================================================

      .addCase(
        fetchOverallSummaryReportThunk.pending,
        (state) => {
          state.overallSummary.loading = true;
          state.overallSummary.error = null;
        }
      )

      .addCase(
        fetchOverallSummaryReportThunk.fulfilled,
        (state, action) => {
          state.overallSummary.loading = false;
          state.overallSummary.loaded = true;
          state.overallSummary.error = null;

          state.overallSummary.filters =
            action.payload?.filters || {
              from_date: "",
              to_date: "",
              total_days: 0,
              pan_ids: [],
              export: 0,
            };

          state.overallSummary.panchayathSummary =
            action.payload?.panchayathSummary || {
              rows: [],
              pagination: { ...initialPagination },
            };

          state.overallSummary.agentSummary =
            action.payload?.agentSummary || {
              rows: [],
              pagination: { ...initialPagination },
            };

          state.overallSummary.officeUserSummary =
            action.payload?.officeUserSummary || {
              rows: [],
              pagination: { ...initialPagination },
            };
        }
      )

      .addCase(
        fetchOverallSummaryReportThunk.rejected,
        (state, action) => {
          state.overallSummary.loading = false;
          state.overallSummary.loaded = true;

          state.overallSummary.error =
            action.payload ||
            "Failed to load overall summary report";

          state.overallSummary.panchayathSummary.rows = [];
          state.overallSummary.agentSummary.rows = [];
          state.overallSummary.officeUserSummary.rows = [];
        }
      );
  },
});

export const {
  clearZeroBillReport,
  clearZeroBillReportError,
  clearOverallSummaryReport,
  clearOverallSummaryReportError,
} = zeroBillReportSlice.actions;

export default zeroBillReportSlice.reducer;